import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import { ArrowDown, Check, Copy, HeartHandshake, Lock, Mail, Waves } from "lucide-react";
import { Logo } from "@/components/Logo";
import VisitorForm from "@/components/VisitorForm";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1547382002-b908c9367d83?crop=entropy&cs=srgb&fm=jpg&q=85&fit=crop&w=1200&h=1500";
const WATER_IMG = "https://images.unsplash.com/photo-1623275563401-6f9c556355a8?crop=entropy&cs=srgb&fm=jpg&q=85";
const COMMUNITY_IMG = "https://images.unsplash.com/photo-1695938746747-ec185ea81325?crop=entropy&cs=srgb&fm=jpg&q=85";

const MARQUEE_ITEMS = ["One Person", "One Invitation", "One Connection", "Welcome Home to LFCC"];
const ETRANSFER_EMAIL = "lfccgiving@gmail.com";
const PAYPAL_URL = "https://www.paypal.com/ncp/payment/CCDZJKRCRP6RG";

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function GiveSection() {
  const [copied, setCopied] = useState(false);
  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(ETRANSFER_EMAIL);
      setCopied(true);
      toast.success("Email copied — paste it into your banking app");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(`Please copy manually: ${ETRANSFER_EMAIL}`);
    }
  };

  return (
    <section id="give" className="py-16 sm:py-24 bg-parchment/60 border-t border-border" data-testid="give-section">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="text-center mb-12"
        >
          <p className="text-xs uppercase tracking-[0.25em] font-mono text-ember mb-3">Giving</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-river">Partner with the Fountain</h2>
          <p className="text-muted-foreground mt-4 max-w-lg mx-auto leading-relaxed">
            Your generosity helps us reach London with the love of Christ — one person, one invitation, one connection at a time.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-5">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="bg-white rounded-3xl border border-border p-7 shadow-lg shadow-river/5"
            data-testid="etransfer-card"
          >
            <div className="w-11 h-11 rounded-2xl bg-water/10 flex items-center justify-center mb-4">
              <Mail className="w-5 h-5 text-water" />
            </div>
            <h3 className="font-serif text-2xl text-river mb-2">Interac e-Transfer</h3>
            <p className="text-sm text-muted-foreground mb-4">Send your gift directly from your banking app to:</p>
            <div className="flex items-center gap-2 bg-parchment/70 border border-border rounded-xl px-4 py-3 mb-3">
              <span className="font-mono text-sm text-river flex-1 break-all" data-testid="etransfer-email">{ETRANSFER_EMAIL}</span>
              <button
                onClick={copyEmail}
                data-testid="copy-etransfer-button"
                className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-river hover:bg-river-light rounded-full px-3.5 py-2 transition-colors"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">Feel free to include your name in the transfer message so we can thank you.</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
            className="bg-white rounded-3xl border border-border p-7 shadow-lg shadow-river/5"
            data-testid="paypal-card"
          >
            <div className="w-11 h-11 rounded-2xl bg-ember/10 flex items-center justify-center mb-4">
              <HeartHandshake className="w-5 h-5 text-ember" />
            </div>
            <h3 className="font-serif text-2xl text-river mb-2">PayPal</h3>
            <p className="text-sm text-muted-foreground mb-4">Give securely online in just a few taps:</p>
            <a
              href={PAYPAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="paypal-give-button"
              className="inline-flex items-center justify-center gap-2 w-full min-h-[48px] rounded-full bg-ember text-white text-sm font-semibold hover:bg-ember-light transition-[background-color,transform] active:scale-[0.98] shadow-md shadow-ember/25"
            >
              Give via PayPal
            </a>
            <p className="text-xs text-muted-foreground mt-3 font-mono break-all">Secure giving page · paypal.com — Living Fountain Christian Centre</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-background texture-grain" data-testid="landing-page">
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-alabaster/80 border-b border-border/60">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="brand-link">
            <Logo size={42} />
            <span className="leading-tight">
              <span className="block font-serif font-semibold text-river text-lg">LFCC</span>
              <span className="hidden sm:block text-[10px] uppercase tracking-[0.2em] font-mono text-muted-foreground">Living Fountain</span>
            </span>
          </a>
          <div className="flex items-center gap-2">
            <a href="/admin" data-testid="leader-login-link" className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-river px-3 py-2 rounded-full transition-colors">
              <Lock className="w-3.5 h-3.5" /> Leader Login
            </a>
            <button onClick={() => scrollTo("give")} data-testid="header-give-button" className="min-h-[44px] px-4 rounded-full text-sm font-semibold text-river hover:bg-parchment transition-colors">
              Give
            </button>
            <button onClick={() => scrollTo("connect")} data-testid="header-connect-button" className="min-h-[44px] px-5 rounded-full bg-river text-alabaster text-sm font-semibold hover:bg-river-light transition-[background-color,transform] active:scale-[0.97]">
              Connect With Us
            </button>
          </div>
        </div>
      </header>

      <section ref={heroRef} className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full bg-water/10 blur-3xl animate-water" />
        <div className="absolute top-1/2 -left-32 w-[380px] h-[380px] rounded-full bg-ember/10 blur-3xl animate-water" style={{ animationDelay: "-4s" }} />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center relative">
          <motion.div style={{ y: textY }}>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.6 }}
              className="text-xs uppercase tracking-[0.25em] font-mono text-ember mb-6"
              data-testid="hero-eyebrow"
            >
              Living Fountain Christian Centre · London, Ontario
            </motion.p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-river tracking-tight" data-testid="hero-heading">
              <MaskedLine delay={0.25}>Welcome</MaskedLine>
              <MaskedLine delay={0.38} className="italic text-water">home.</MaskedLine>
              <MaskedLine delay={0.51} className="text-3xl sm:text-4xl lg:text-5xl mt-4 text-foreground/80">We're glad you're here.</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.7 }}
              className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-md"
              data-testid="hero-subtext"
            >
              Please take a moment to connect with us. What you share helps us serve you — and, only if you choose, stay connected with you.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <button onClick={() => scrollTo("connect")} data-testid="hero-connect-button" className="group inline-flex items-center gap-3 min-h-[52px] px-8 rounded-full bg-ember text-white font-semibold hover:bg-ember-light transition-[background-color,transform] active:scale-[0.97] shadow-lg shadow-ember/25">
                Fill the Connection Card
                <ArrowDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
              </button>
              <span className="text-xs font-mono text-muted-foreground">Takes about 2 minutes</span>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.45, duration: 0.9, ease: "easeOut" }}
            className="relative"
          >
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] max-h-[560px] w-full shadow-2xl shadow-river/20 border-4 border-white">
              <motion.img src={HERO_IMG} alt="Worship at Living Fountain Christian Centre" style={{ y: imgY }} className="w-full h-[120%] object-cover" data-testid="hero-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-river-deep/60 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl p-4">
                <p className="font-serif italic text-white text-lg leading-snug">"…and God shall wipe away all tears from their eyes…"</p>
                <p className="text-white/70 text-xs font-mono mt-1.5 tracking-widest">REVELATION 7:17</p>
              </div>
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl shadow-river/10 border border-border px-5 py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-water/10 flex items-center justify-center">
                <Waves className="w-5 h-5 text-water" />
              </div>
              <div>
                <p className="text-sm font-semibold text-river">Every Sunday</p>
                <p className="text-xs text-muted-foreground">You are welcome here</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="border-y border-border bg-river py-4 overflow-hidden" data-testid="marquee-banner">
        <div className="flex whitespace-nowrap animate-marquee">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex shrink-0">
              {Array.from({ length: 4 }).flatMap((_, i) =>
                MARQUEE_ITEMS.map((item, j) => (
                  <span key={`${i}-${j}`} className="mx-6 font-serif italic text-xl text-alabaster/80 flex items-center gap-12">
                    {item} <span className="text-ember-light not-italic text-sm">✦</span>
                  </span>
                ))
              )}
            </div>
          ))}
        </div>
      </div>

      <section id="connect" className="relative py-16 sm:py-24">
        <div className="absolute inset-x-0 top-0 h-64 overflow-hidden opacity-[0.07] pointer-events-none">
          <img src={WATER_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="max-w-3xl mx-auto px-5 sm:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="text-center mb-10"
          >
            <p className="text-xs uppercase tracking-[0.25em] font-mono text-ember mb-3">Visitor Connection Card</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-river">Let's stay connected</h2>
            <p className="text-muted-foreground mt-4 max-w-lg mx-auto">
              Six short sections — no wrong answers. Only share what you're comfortable with.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <VisitorForm />
          </motion.div>
        </div>
      </section>

      <GiveSection />

      <footer className="relative bg-river-deep text-alabaster overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img src={COMMUNITY_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-river-deep/70 to-river-deep/95" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="max-w-xl mx-auto mb-14 rounded-3xl border border-white/20 bg-white/10 backdrop-blur-md p-7 sm:p-9"
            data-testid="survey-cta-banner"
          >
            <p className="text-xs uppercase tracking-[0.25em] font-mono text-ember-light mb-2">Visited us?</p>
            <p className="font-serif text-2xl sm:text-3xl mb-2">How was your experience?</p>
            <p className="text-alabaster/70 text-sm mb-5">One minute of honest feedback helps us serve every guest better.</p>
            <a
              href="/survey"
              data-testid="survey-cta-button"
              className="inline-flex items-center gap-2 min-h-[52px] px-8 rounded-full bg-ember text-white font-semibold hover:bg-ember-light transition-[background-color,transform] active:scale-[0.97] shadow-lg shadow-ember/30"
            >
              Share Your Experience <ArrowDown className="w-4 h-4 -rotate-90" />
            </a>
          </motion.div>
          <div className="flex justify-center mb-6"><Logo size={84} /></div>
          <p className="font-serif text-3xl sm:text-4xl mb-3">Living Fountain Christian Centre</p>
          <p className="text-alabaster/60 text-sm font-mono tracking-[0.2em] uppercase mb-8">London, Ontario</p>
          <p className="text-alabaster/70 max-w-md mx-auto text-sm leading-relaxed">
            One Person. One Invitation. One Connection. We can't wait to see you again next Sunday.
          </p>
          <p className="text-alabaster/40 text-xs mt-8">
            <a href="/privacy" className="hover:text-alabaster transition-colors" data-testid="footer-privacy-link">Privacy Policy</a>
            <span className="mx-3">·</span>
            <a href="/terms" className="hover:text-alabaster transition-colors" data-testid="footer-terms-link">Terms &amp; Conditions</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
